import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import moment from 'moment';

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  CheckCircle2,
  XCircle,
  Download,
  FileText,
  DollarSign,
  Loader2,
  Pen,
  RotateCcw
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function EstimateAcceptPage() {
  const { estimateId } = useParams();
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Acceptance state
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [acceptForm, setAcceptForm] = useState({
    customer_name: "",
    customer_email: "",
    notes: ""
  });
  const [rejectReason, setRejectReason] = useState("");
  const [success, setSuccess] = useState(false);
  
  // Signature canvas
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    fetchEstimate();
  }, [estimateId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (estimate) {
      setAcceptForm({
        customer_name: estimate.customer_name || "",
        customer_email: estimate.customer_email || "",
        notes: ""
      });
    }
  }, [estimate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchEstimate = async () => {
    try {
      const response = await axios.get(`${API}/estimates/public/${estimateId}`);
      setEstimate(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load estimate");
    } finally {
      setLoading(false);
    }
  };

  // Signature canvas functions
  const initCanvas = () => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  useEffect(() => {
    if (showAcceptModal) {
      setTimeout(initCanvas, 100);
    }
  }, [showAcceptModal]); // eslint-disable-line react-hooks/exhaustive-deps

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    
    const x = (e.touches?.[0]?.clientX || e.clientX) - rect.left;
    const y = (e.touches?.[0]?.clientY || e.clientY) - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasSignature(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    
    const x = (e.touches?.[0]?.clientX || e.clientX) - rect.left;
    const y = (e.touches?.[0]?.clientY || e.clientY) - rect.top;
    
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    initCanvas();
    setHasSignature(false);
  };

  const getSignatureData = () => {
    return canvasRef.current?.toDataURL('image/png');
  };

  const handleAccept = async () => {
    if (!acceptForm.customer_name || !acceptForm.customer_email) {
      alert("Please fill in your name and email");
      return;
    }
    
    if (!hasSignature) {
      alert("Please sign to accept the estimate");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/estimates/public/${estimateId}/accept`, {
        customer_name: acceptForm.customer_name,
        customer_email: acceptForm.customer_email,
        signature: getSignatureData(),
        notes: acceptForm.notes
      });
      
      setSuccess(true);
      setShowAcceptModal(false);
      setEstimate({ ...estimate, status: 'accepted', is_accepted: true });
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to accept estimate");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/estimates/public/${estimateId}/reject`, {
        reason: rejectReason
      });
      
      setShowRejectModal(false);
      setEstimate({ ...estimate, status: 'rejected' });
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to reject estimate");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadPDF = () => {
    window.open(`${API}/estimates/public/${estimateId}/pdf`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="py-8 text-center">
            <XCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
            <p className="text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isAccepted = estimate?.status === 'accepted' || estimate?.is_accepted;
  const isRejected = estimate?.status === 'rejected';
  const canRespond = !isAccepted && !isRejected;

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-indigo-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Sage Kitchen and Bath
          </h1>
          <p className="text-slate-500 mt-2">Estimate {estimate?.estimate_number}</p>
        </div>

        {/* Status Banner */}
        {isAccepted && (
          <Card className="border-green-200 bg-green-50" data-testid="estimate-accepted-confirmation">
            <CardContent className="py-4 text-center">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-green-500" />
              <h2 className="text-xl font-bold text-green-700">Estimate Accepted!</h2>
              <p className="text-green-600">Thank you for your business. We will be in touch shortly.</p>
            </CardContent>
          </Card>
        )}

        {isRejected && (
          <Card className="border-red-200 bg-red-50" data-testid="estimate-declined-confirmation">
            <CardContent className="py-4 text-center">
              <XCircle className="w-12 h-12 mx-auto mb-2 text-red-500" />
              <h2 className="text-xl font-bold text-red-700">Estimate Declined</h2>
              <p className="text-red-600">Thank you for considering us. Please reach out if you change your mind.</p>
            </CardContent>
          </Card>
        )}

        {/* Main Estimate Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                {estimate?.category}
                {estimate?.sub_category && ` - ${estimate.sub_category}`}
              </CardTitle>
              <Badge className={
                isAccepted ? "bg-green-100 text-green-700" :
                isRejected ? "bg-red-100 text-red-700" :
                "bg-blue-100 text-blue-700"
              }>
                {estimate?.status?.toUpperCase()}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Customer Info */}
            <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="text-sm text-slate-500">Prepared for</p>
                <p className="font-medium">{estimate?.customer_name}</p>
                <p className="text-sm text-slate-600">{estimate?.customer_email}</p>
                <p className="text-sm text-slate-600">{estimate?.customer_phone}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-500">Date</p>
                <p className="font-medium">{moment(estimate?.created_at).format('MMM D, YYYY')}</p>
                <p className="text-sm text-slate-600">Valid for {estimate?.validity_days} days</p>
              </div>
            </div>

            {/* Project Details */}
            {estimate?.form_data && Object.keys(estimate.form_data).some(k => estimate.form_data[k]) && (
              <div>
                <h3 className="font-semibold mb-2">Project Details</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {estimate.form_data.door_style && (
                    <p><span className="text-slate-500">Door Style:</span> {estimate.form_data.door_style}</p>
                  )}
                  {estimate.form_data.door_color && (
                    <p><span className="text-slate-500">Door Color:</span> {estimate.form_data.door_color}</p>
                  )}
                  {estimate.form_data.countertop_type && estimate.form_data.countertop_type !== 'NO' && (
                    <p><span className="text-slate-500">Countertop:</span> {estimate.form_data.countertop_type}</p>
                  )}
                </div>
              </div>
            )}

            {/* Line Items */}
            <div>
              <h3 className="font-semibold mb-2">Items</h3>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-3">Description</th>
                      <th className="text-center p-3">Qty</th>
                      <th className="text-right p-3">Price</th>
                      <th className="text-right p-3">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {estimate?.line_items?.map((item, index) => (
                      <tr key={index} className="border-t">
                        <td className="p-3">
                          {item.description}
                          {item.width && item.height && (
                            <span className="text-slate-500 text-xs ml-2">
                              ({item.width}" x {item.height}")
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-center">{item.quantity}</td>
                        <td className="p-3 text-right">${item.unit_price?.toFixed(2)}</td>
                        <td className="p-3 text-right font-medium">${item.total?.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Totals */}
            <div className="border-t pt-4">
              <div className="flex justify-end">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Subtotal</span>
                    <span>${estimate?.subtotal?.toFixed(2)}</span>
                  </div>
                  {estimate?.discount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Discount</span>
                      <span>-${estimate.discount?.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-slate-600">Tax ({estimate?.tax_rate}%)</span>
                    <span>${estimate?.tax_amount?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2">
                    <span>Total</span>
                    <span className="text-indigo-600">${estimate?.total?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between bg-amber-50 p-2 rounded-lg text-amber-800">
                    <span>Deposit Required ({estimate?.deposit_percentage}%)</span>
                    <span className="font-bold">${estimate?.deposit_required?.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Terms */}
            {estimate?.terms_and_conditions && (
              <div>
                <h3 className="font-semibold mb-2">Terms & Conditions</h3>
                <p className="text-sm text-slate-600 bg-slate-50 p-4 rounded-lg whitespace-pre-wrap">
                  {estimate.terms_and_conditions}
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className={`grid gap-3 pt-4 border-t ${canRespond ? "grid-cols-1 sm:grid-cols-3" : "grid-cols-1"}`}>
              <Button variant="outline" onClick={handleDownloadPDF} className="w-full" data-testid="estimate-download-pdf-button">
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
              
              {canRespond && (
                <>
                  <Button 
                    variant="outline" 
                    onClick={() => setShowRejectModal(true)}
                    className="w-full border-red-200 text-red-600 hover:bg-red-50"
                    data-testid="estimate-decline-button"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Decline
                  </Button>
                  <Button 
                    onClick={() => setShowAcceptModal(true)}
                    className="w-full bg-green-600 hover:bg-green-700"
                    data-testid="estimate-accept-button"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Accept Estimate
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-sm text-slate-500">
          <p>Sage Kitchen and Bath</p>
          <p data-testid="estimate-company-contact">info@sagekitchenandbath.ca | (905)-874-9959</p>
        </div>
      </div>

      {/* Accept Modal */}
      <Dialog open={showAcceptModal} onOpenChange={setShowAcceptModal}>
        <DialogContent className="max-h-[85vh] overflow-y-auto max-w-lg">
          <DialogHeader>
            <DialogTitle>Accept Estimate</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Your Name *</Label>
                <Input
                  value={acceptForm.customer_name}
                  onChange={(e) => setAcceptForm({ ...acceptForm, customer_name: e.target.value })}
                  placeholder="Full name"
                  data-testid="estimate-accept-name-input"
                />
              </div>
              <div>
                <Label>Your Email *</Label>
                <Input
                  type="email"
                  value={acceptForm.customer_email}
                  onChange={(e) => setAcceptForm({ ...acceptForm, customer_email: e.target.value })}
                  placeholder="Email address"
                  data-testid="estimate-accept-email-input"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Signature *</Label>
                <Button variant="ghost" size="sm" onClick={clearSignature} data-testid="estimate-signature-clear-button">
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              </div>
              <div className="border rounded-lg p-1 bg-white">
                <canvas
                  ref={canvasRef}
                  width={400}
                  height={150}
                  className="w-full border rounded cursor-crosshair touch-none"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                  data-testid="estimate-signature-canvas"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                <Pen className="w-3 h-3" />
                Draw your signature above
              </p>
            </div>

            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={acceptForm.notes}
                onChange={(e) => setAcceptForm({ ...acceptForm, notes: e.target.value })}
                placeholder="Any additional notes..."
                rows={2}
                data-testid="estimate-accept-notes-input"
              />
            </div>

            <div className="bg-slate-50 p-3 rounded-lg text-sm">
              <p>By signing, you agree to:</p>
              <ul className="list-disc list-inside text-slate-600 mt-1">
                <li>The estimate total of <strong>${estimate?.total?.toFixed(2)}</strong></li>
                <li>A deposit of <strong>${estimate?.deposit_required?.toFixed(2)}</strong> to begin work</li>
                <li>The terms and conditions outlined above</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAcceptModal(false)} data-testid="estimate-accept-cancel-button">
              Cancel
            </Button>
            <Button 
              onClick={handleAccept} 
              disabled={submitting}
              className="bg-green-600 hover:bg-green-700"
              data-testid="estimate-accept-confirm-button"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Accept & Sign
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent className="max-h-[85vh] overflow-y-auto max-w-md">
          <DialogHeader>
            <DialogTitle>Decline Estimate</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-slate-600">
              We're sorry to hear that. Would you like to share why you're declining?
            </p>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Optional: Let us know how we can improve..."
              rows={3}
              data-testid="estimate-decline-reason-input"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectModal(false)} data-testid="estimate-decline-cancel-button">
              Cancel
            </Button>
            <Button 
              onClick={handleReject} 
              disabled={submitting}
              variant="destructive"
              data-testid="estimate-decline-confirm-button"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                "Confirm Decline"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
